import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { AddressCandidate } from '@agentic-flow-studio/shared/types/job-proposal';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { ArrowLeft, Sparkles } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  type EditedFieldsShape,
  IngestProposalsTable,
} from '../components/ingest/IngestProposalsTable';
import { IngestUploadPanel, type VerticalKey } from '../components/ingest/IngestUploadPanel';

export default function JobIngestPage() {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const serviceDate = searchParams.get('serviceDate') || new Date().toISOString().split('T')[0];

  // Upload/extract state
  const [pasteText, setPasteText] = useState<string>('');
  const [selectedImageBase64, setSelectedImageBase64] = useState<string | null>(null);
  const [selectedImageMime, setSelectedImageMime] = useState<string>('image/png');
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [verticalKey, setVerticalKey] = useState<VerticalKey>('trades');
  const [customGuidelines, setCustomGuidelines] = useState<string>('');

  // Review state
  const [selectedCandidates, setSelectedCandidates] = useState<Record<number, AddressCandidate>>(
    {}
  );
  const [manualAddresses, setManualAddresses] = useState<Record<number, string>>({});
  const [editedFields, setEditedFields] = useState<Record<number, EditedFieldsShape>>({});
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: jobPlanSettings } = trpc.jobPlan.getSettings.useQuery();

  useEffect(() => {
    if (jobPlanSettings) {
      setVerticalKey(jobPlanSettings.verticalKey);
      if (jobPlanSettings.customRules) {
        setCustomGuidelines(jobPlanSettings.customRules);
      }
    }
  }, [jobPlanSettings]);

  const { data: proposals = [], isLoading: isLoadingProposals } =
    trpc.jobPlan.listProposals.useQuery();

  const { data: skills = [] } = trpc.jobPlan.skills.list.useQuery();
  const skillMap = new Map(skills.map((s) => [s.id, s]));

  const { data: operators = [] } = trpc.jobPlan.operators.list.useQuery({ activeOnly: true });

  // Default every staged proposal to selected (opt-out); re-syncs whenever the staged
  // set changes (new extraction batch, a confirm, or a reject), not on every re-render.
  const proposalIdsKey = proposals.map((p) => p.id).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: proposalIdsKey is the deliberate dependency -- it's a stable primitive summary of proposals' identity, re-deriving from proposals directly would reset the user's manual unchecks on every background refetch.
  useEffect(() => {
    setSelectedIds(new Set(proposals.map((p) => p.id)));
  }, [proposalIdsKey]);

  const ingestMutation = trpc.jobPlan.ingestJobs.useMutation({
    onSuccess: (data) => {
      toast.success(`Extracted & staged ${data.proposals.length} job proposal(s) with AI`);
      setPasteText('');
      setSelectedImageBase64(null);
      setSelectedFileName(null);
      utils.jobPlan.listProposals.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const confirmProposalsMutation = trpc.jobPlan.confirmProposals.useMutation({
    onSuccess: (data) => {
      const succeeded = data.results.filter((r) => r.success).length;
      const failed = data.results.filter((r) => !r.success).length;
      if (succeeded > 0) {
        toast.success(`Successfully confirmed and scheduled ${succeeded} job(s)`);
      }
      if (failed > 0) {
        toast.error(`Failed to confirm ${failed} job(s)`);
      }
      utils.jobPlan.listProposals.invalidate();
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
      utils.jobPlan.jobs.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const rejectProposalMutation = trpc.jobPlan.rejectProposal.useMutation({
    onSuccess: () => {
      toast.info('Proposal removed');
      utils.jobPlan.listProposals.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleTriggerExtraction = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedImageBase64) {
      const isPdf =
        selectedImageMime === 'application/pdf' || selectedFileName?.toLowerCase().endsWith('.pdf');
      ingestMutation.mutate({
        sourceKind: isPdf ? 'file' : 'image',
        sourceReference: selectedFileName || (isPdf ? 'PDF Document' : 'Screenshot'),
        imageBase64: selectedImageBase64,
        mimeType: selectedImageMime,
        verticalKey,
        customGuidelines: customGuidelines.trim() || undefined,
        text: pasteText.trim() || undefined,
        serviceDate,
      });
    } else if (pasteText.trim()) {
      ingestMutation.mutate({
        sourceKind: selectedFileName?.endsWith('.csv') ? 'file' : 'text',
        sourceReference: selectedFileName || undefined,
        text: pasteText,
        verticalKey,
        customGuidelines: customGuidelines.trim() || undefined,
        serviceDate,
      });
    } else {
      toast.error('Please provide a PDF document, screenshot, CSV file, or paste job details');
    }
  };

  const buildConfirmItem = (pId: number) => {
    const p = proposals.find((item) => item.id === pId);
    if (!p) return null;

    const chosenCand = selectedCandidates[p.id];
    const manualAddr = manualAddresses[p.id];
    const overrides = editedFields[p.id] || {};
    const operatorProfileId =
      overrides.operatorProfileId !== undefined
        ? overrides.operatorProfileId
        : p.suggestedOperatorId;

    return {
      proposalId: p.id,
      title: overrides.title ?? p.title,
      description: overrides.description ?? p.description ?? undefined,
      formattedAddress:
        chosenCand?.formattedAddress || manualAddr || p.formattedAddress || undefined,
      latitude: chosenCand?.latitude ?? p.latitude ?? undefined,
      longitude: chosenCand?.longitude ?? p.longitude ?? undefined,
      googlePlaceId: chosenCand?.googlePlaceId ?? p.googlePlaceId ?? undefined,
      contactName: overrides.contactName ?? p.contactName ?? undefined,
      contactPhone: overrides.contactPhone ?? p.contactPhone ?? undefined,
      contactEmail: overrides.contactEmail ?? p.contactEmail ?? undefined,
      contactCompany: overrides.contactCompany ?? p.contactCompany ?? undefined,
      externalRef: overrides.externalRef ?? p.externalRef ?? undefined,
      serviceDate: overrides.serviceDate ?? p.serviceDate ?? serviceDate,
      estimatedDurationSeconds: overrides.estimatedDurationMinutes
        ? overrides.estimatedDurationMinutes * 60
        : p.estimatedDurationSeconds,
      skillIds: p.suggestedSkillIds,
      operatorProfileId: operatorProfileId ?? undefined,
    };
  };

  const handleConfirmSingle = (pId: number) => {
    const item = buildConfirmItem(pId);
    if (!item) return;
    confirmProposalsMutation.mutate({ proposals: [item] });
  };

  const handleConfirmSelected = () => {
    const items = Array.from(selectedIds)
      .map((id) => buildConfirmItem(id))
      .filter((item): item is NonNullable<typeof item> => item !== null);
    if (items.length === 0) return;
    confirmProposalsMutation.mutate({ proposals: items });
  };

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === proposals.length ? new Set() : new Set(proposals.map((p) => p.id))
    );
  };

  return (
    <div className="flex flex-col h-svh w-full overflow-hidden bg-background">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border/40 bg-card shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <span className="font-bold text-base">AI Job Plan Ingest & Staging</span>
            <p className="text-[11px] text-muted-foreground font-normal">
              Drop screenshots, paste legacy software exports, and auto-extract jobs with contacts.
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">
          {proposals.length} staged
        </Badge>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
        <IngestUploadPanel
          orgId={orgId}
          workspaceId={workspaceId}
          verticalKey={verticalKey}
          onVerticalKeyChange={setVerticalKey}
          customGuidelines={customGuidelines}
          onCustomGuidelinesChange={setCustomGuidelines}
          pasteText={pasteText}
          onPasteTextChange={setPasteText}
          selectedImageBase64={selectedImageBase64}
          selectedImageMime={selectedImageMime}
          selectedFileName={selectedFileName}
          onImageSelected={(base64, mime, fileName) => {
            setSelectedImageBase64(base64);
            setSelectedImageMime(mime);
            setSelectedFileName(fileName);
          }}
          onDocumentSelected={(fileName, text) => {
            setSelectedFileName(fileName);
            setPasteText(text);
          }}
          onClearImage={() => {
            setSelectedImageBase64(null);
            setSelectedFileName(null);
          }}
          onSubmit={handleTriggerExtraction}
          isExtracting={ingestMutation.isPending}
        />

        <IngestProposalsTable
          proposals={proposals}
          isLoading={isLoadingProposals}
          skillMap={skillMap}
          operators={operators}
          serviceDate={serviceDate}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onToggleSelectAll={toggleSelectAll}
          onConfirmSelected={handleConfirmSelected}
          onConfirmSingle={handleConfirmSingle}
          onReject={(id) => rejectProposalMutation.mutate({ id })}
          isConfirming={confirmProposalsMutation.isPending}
          expandedId={expandedId}
          onToggleExpanded={(id) => setExpandedId((prev) => (prev === id ? null : id))}
          selectedCandidates={selectedCandidates}
          onSelectCandidate={(id, cand) =>
            setSelectedCandidates((prev) => ({ ...prev, [id]: cand }))
          }
          manualAddresses={manualAddresses}
          onManualAddressChange={(id, value) =>
            setManualAddresses((prev) => ({ ...prev, [id]: value }))
          }
          editedFields={editedFields}
          onEditField={(id, patch) =>
            setEditedFields((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
          }
        />
      </div>
    </div>
  );
}
